<script setup lang="ts">
import GooglePlayBadge from '@images/logos/google-play-badge.svg?component';

declare module 'nuxt/app' {
  interface PageMeta {
    hideAppBanners?: boolean;
  }
}

const STORAGE_KEY = 'mobileAppBannerDismissed';

const config = useRuntimeConfig();
const route = useRoute();
const isDismissed = ref(true);
const isAndroid = ref(false);

const showBanner = computed(() => !isDismissed.value && isAndroid.value && !route.meta.hideAppBanners);

useHead({
  meta: computed(() =>
    route.meta.hideAppBanners ? [] : [{ name: 'apple-itunes-app', content: 'app-id=6749672159' }],
  ),
});

const openPlayStore = () => {
  window.open(config.public.mobileApp.googlePlayUrl, '_blank');
};

const dismiss = () => {
  isDismissed.value = true;
  sessionStorage.setItem(STORAGE_KEY, 'true');
};

onMounted(() => {
  isDismissed.value = !!sessionStorage.getItem(STORAGE_KEY);

  const userAgentData = (navigator as Navigator & { userAgentData?: { platform: string } }).userAgentData;
  if (userAgentData?.platform) {
    isAndroid.value = userAgentData.platform.toLowerCase() === 'android';
  } else {
    isAndroid.value = /android/i.test(navigator.userAgent);
  }
});
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 -translate-y-full"
    leave-to-class="opacity-0 -translate-y-full"
  >
    <div
      v-if="showBanner"
      class="fixed left-0 right-0 top-0 z-[901] w-full border-b border-neutral-20 bg-white px-4 py-3"
    >
      <div class="flex items-center justify-between gap-3">
        <button
          type="button"
          class="flex size-8 shrink-0 items-center justify-center rounded-full text-neutral-60 transition-colors hover:bg-neutral-100/10 hover:text-neutral-100"
          aria-label="Close banner"
          @click="dismiss"
        >
          <FontAwesomeIcon
            icon="fa-xmark"
            class="text-lg"
          />
        </button>

        <div class="flex min-w-0 flex-1 items-center gap-3">
          <img
            src="/favicon/favicon.svg"
            alt="Medmastery"
            class="size-10 shrink-0 rounded-lg"
          >
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold text-neutral-100">
              Medmastery
            </p>
            <p class="truncate text-xs text-neutral-60">
              Learn medicine on the go
            </p>
          </div>
        </div>

        <button
          type="button"
          class="shrink-0"
          @click="openPlayStore"
        >
          <GooglePlayBadge class="h-10 w-auto" />
        </button>
      </div>
    </div>
  </Transition>
</template>
